import { useEffect } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle2,
  Sparkles,
  BookOpen,
  Users,
  Calculator as CalcIcon,
  HelpCircle,
  Code2,
} from "lucide-react";
import type { ReactNode } from "react";
import { SiteHeader, SiteFooter } from "@/components/tools/ToolShell";
import type { CalculatorMeta } from "@/lib/calculators-catalog";
import { relatedCalculators } from "@/lib/calculators-catalog";
import type { CalculatorContent } from "@/lib/calculator-content";
import type { Language } from "@/lib/i18n";

interface CalculatorShellProps {
  calc: CalculatorMeta;
  content: CalculatorContent;
  lang?: Language;
  children: ReactNode;
}

export function CalculatorShell({ calc, content, lang = "en", children }: CalculatorShellProps) {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const related = relatedCalculators(calc.slug);

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <SiteHeader lang={lang} />

      <main className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 pb-12">
        {/* Breadcrumbs */}
        <nav className="no-print pt-6 text-sm">
          <Link
            to={lang === "en" ? "/calculators" : `/${lang === "pt-BR" ? "pt-br" : lang}/calculators`}
            className="inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground font-semibold"
          >
            <ArrowLeft className="size-4" /> {lang === "es" ? "Todas las Calculadoras" : lang === "de" ? "Alle Online-Rechner" : lang === "fr" ? "Tous les calculateurs" : lang === "pt-BR" ? "Todas as Calculadoras" : "All Online Calculators"}
          </Link>
        </nav>

        {/* SEO Header Section */}
        <div className="no-print pt-4 pb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
            {calc.category}
          </p>
          <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl tracking-tight text-foreground">
            {content.h1Title || calc.name}
          </h1>
          <p
            className="mt-2.5 max-w-3xl text-base text-muted-foreground leading-relaxed"
            dangerouslySetInnerHTML={{ __html: content.intro }}
          />
        </div>

        {/* Interactive Calculator Tool Component */}
        {children}

        {/* SEO Rich Content Sections */}
        <div className="no-print mt-14 space-y-10">
          {/* Formulas Section */}
          {content.formulas && content.formulas.length > 0 && (
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2 mb-4">
                <Code2 className="size-5 text-primary" /> Mathematical Formulas & Calculation Methods
              </h2>
              <div className="grid gap-4 sm:grid-cols-3">
                {content.formulas.map((f, idx) => (
                  <div key={idx} className="p-4 rounded-xl border bg-secondary/30 space-y-2">
                    <h3 className="font-bold text-sm text-foreground">{f.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: f.description }} />
                    <div className="p-2.5 rounded-lg bg-background border text-xs font-mono font-bold text-primary">
                      {f.formulaText}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Worked Examples */}
          {content.workedExamples && content.workedExamples.length > 0 && (
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border space-y-4">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2 mb-2">
                <BookOpen className="size-5 text-primary" /> Worked Step-by-Step Examples
              </h2>
              <div className="grid gap-6 md:grid-cols-2">
                {content.workedExamples.map((ex, idx) => (
                  <div key={idx} className="p-5 rounded-2xl bg-secondary/20 border space-y-3">
                    <h3 className="font-bold text-base text-foreground">{ex.title}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground italic bg-background/60 p-3 rounded-xl border">
                      "{ex.problem}"
                    </p>
                    <div className="space-y-1.5 text-xs sm:text-sm text-foreground">
                      <p className="font-bold uppercase tracking-wider text-[11px] text-muted-foreground">Steps:</p>
                      <ul className="space-y-1 list-disc list-inside text-muted-foreground">
                        {ex.solutionSteps.map((step, sIdx) => (
                          <li key={sIdx} dangerouslySetInnerHTML={{ __html: step }} />
                        ))}
                      </ul>
                    </div>
                    <div className="p-3 rounded-xl bg-primary/10 border border-primary/20 text-xs font-bold text-foreground">
                      Result: {ex.finalResult}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* What is Section */}
          {content.whatIs && content.whatIs.length > 0 && (
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2 mb-4">
                <BookOpen className="size-5 text-primary" /> What is {calc.name}?
              </h2>
              <div className="space-y-4 text-muted-foreground text-sm sm:text-base leading-relaxed">
                {content.whatIs.map((p, idx) => (
                  <p key={idx} dangerouslySetInnerHTML={{ __html: p }} />
                ))}
              </div>
            </section>
          )}

          {/* How to Use & Key Features */}
          <div className="grid gap-6 md:grid-cols-2">
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2 mb-4">
                <Sparkles className="size-5 text-primary" /> How to Use {calc.name}
              </h2>
              <ol className="space-y-3 text-sm sm:text-base text-muted-foreground">
                {content.howTo.map((step, i) => (
                  <li key={step} className="flex gap-3">
                    <span className="grid size-6 shrink-0 place-items-center rounded-full bg-primary/10 text-primary font-bold text-xs">
                      {i + 1}
                    </span>
                    <span className="leading-snug" dangerouslySetInnerHTML={{ __html: step }} />
                  </li>
                ))}
              </ol>
            </section>

            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2 mb-4">
                <CheckCircle2 className="size-5 text-primary" /> Key Features
              </h2>
              <ul className="space-y-3 text-sm sm:text-base text-muted-foreground">
                {content.features.map((f) => (
                  <li key={f} className="flex gap-2.5 items-start">
                    <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />
                    <span className="leading-snug" dangerouslySetInnerHTML={{ __html: f }} />
                  </li>
                ))}
              </ul>
            </section>
          </div>

          {/* Benefits */}
          {content.benefits && content.benefits.length > 0 && (
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-xl font-bold text-foreground mb-4">
                Benefits of Using {calc.name}
              </h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {content.benefits.map((b, idx) => (
                  <div key={idx} className="flex items-start gap-2.5 p-3 rounded-xl bg-secondary/40 border border-border/50">
                    <CheckCircle2 className="size-4 text-emerald-500 shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm font-medium text-foreground leading-snug" dangerouslySetInnerHTML={{ __html: b }} />
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Who Should Use */}
          {content.useCases && content.useCases.length > 0 && (
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2 mb-4">
                <Users className="size-5 text-primary" /> Practical Use Cases
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {content.useCases.map((uc, idx) => (
                  <div key={idx} className="p-4 rounded-xl border bg-secondary/20">
                    <h3 className="font-bold text-sm text-foreground mb-1">{uc.role}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: uc.description }} />
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Frequently Asked Questions */}
          {content.faq && content.faq.length > 0 && (
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border space-y-4">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2 mb-2">
                <HelpCircle className="size-5 text-primary" /> Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {content.faq.map((item, idx) => (
                  <div key={idx} className="p-4 rounded-xl bg-secondary/30 border border-border/50 space-y-1.5">
                    <h3 className="font-bold text-sm sm:text-base text-foreground">{item.q}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: item.a }} />
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Related Calculators */}
          {related.length > 0 && (
            <section className="pt-6 border-t border-border/80">
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <CalcIcon className="size-5 text-primary" /> {lang === "es" ? "Calculadoras Relacionadas" : lang === "de" ? "Ähnliche Rechner" : lang === "fr" ? "Calculateurs Similaires" : lang === "pt-BR" ? "Calculadoras Relacionadas" : "Explore Related Calculators"}
              </h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {related.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={lang === "en" ? `/calculators/${rel.slug}` : `/${lang === "pt-BR" ? "pt-br" : lang}/calculators/${rel.slug}`}
                    className="p-4 rounded-2xl border bg-card hover:border-primary/50 transition-all space-y-2 group"
                  >
                    <span className="text-xs font-bold text-primary uppercase tracking-wider">{rel.category}</span>
                    <h3 className="font-bold text-base text-foreground group-hover:text-primary transition-colors">
                      {rel.name}
                    </h3>
                    <p className="text-xs text-muted-foreground line-clamp-2">{rel.tagline}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <SiteFooter lang={lang} />
    </div>
  );
}
